import { mixer } from './audio-mixer.js';
import { levelOf, type SoundKind } from './mix.js';

/**
 * Whether Vite bundled this module. Vite resolves a variable `new URL(..., import.meta.url)` as a one-level glob, so a clip inside a species folder
 * (`crow/caw 1`) came out as `.../undefined` in the browser and every wildlife call stayed silent; this glob reaches the subfolders.
 * `import.meta.env` is absent under the Node test runner, which reads the files by path.
 */
const bundled = import.meta.env !== undefined;
const bundledFiles = bundled ? import.meta.glob('../assets/audio/sfx/critters/**/*.mp3', { eager: true, query: '?url', import: 'default' }) as Record<string, string> : {};
const clip = (path: string) => {
  const file = `../assets/audio/sfx/critters/${path}.mp3`;
  return bundled ? bundledFiles[file] : new URL(file, import.meta.url).href;
};

/** The recordings of each wildlife sound, picked at random; `docs/sounds-wanted.md` lists the ones still missing. */
const RECORDINGS = {
  'crow call': [clip('crow/caw 1'), clip('crow/caw 2')],
  'crow double call': [clip('crow/double caw 1')],
  'crow wings': [clip('crow/wings 1'), clip('crow/wings 2')],
  'gull cry': [clip('gull/cry 1')],
  'gull call': [clip('gull/call 1')],
  'gull wings': [clip('gull/wings 1'), clip('gull/wings 2')],
  'sheep bleat': [clip('sheep/bleat 1')],
  'sheep crop': [clip('sheep/crop 1')],
  'rabbit scurry': [clip('rabbit/scurry 1'), clip('rabbit/scurry 2')],
  'deer gallop': [clip('deer/gallop 1')],
  'deer snort': [clip('deer/snort 1')],
} as const;

/** A sound the wildlife makes (`CritterField.onSound`). */
export type CritterCue = keyof typeof RECORDINGS;

const KINDS: Record<CritterCue, SoundKind> = {
  'crow call': 'critter-call', 'crow double call': 'critter-call', 'gull cry': 'critter-call', 'gull call': 'critter-call', 'sheep bleat': 'critter-call',
  'crow wings': 'critter-wings', 'gull wings': 'critter-wings', 'sheep crop': 'critter-munch', 'rabbit scurry': 'critter-wings', 'deer gallop': 'critter-wings', 'deer snort': 'critter-call',
};
/** A cue's own accent over its kind's level: a bleat is a background sound, a gull's long call carries. */
const ACCENT: Partial<Record<CritterCue, number>> = {
  'crow call': .85, 'crow double call': .85, 'gull cry': .85, 'gull call': .72, 'sheep bleat': .5,
  'crow wings': .75, 'gull wings': .75, 'sheep crop': .2, 'rabbit scurry': .35, 'deer gallop': .6, 'deer snort': .1,
};

export const critterRecordings: readonly string[] = Object.values(RECORDINGS).flat();
export const preloadCritterSounds = () => mixer.preloadSfx([...critterRecordings]);

/** Plays one recording of `cue`, `strength` (0..1) quieter with distance from the camera's focus and `pan` across the screen. */
export function playCritter(cue: CritterCue, strength: number, pan = 0, random: () => number = Math.random) {
  const urls = RECORDINGS[cue], kind = KINDS[cue];
  mixer.playEffect(urls[Math.min(urls.length - 1, Math.floor(random() * urls.length))], kind, levelOf(kind) * (ACCENT[cue] ?? 1) * strength, .95 + random() * .1, pan);
}
